/**
 * @file event-dispatcher.ts
 * @description Coordinates canvas events by delegating to specialized handlers and managing global input listeners.
 */

import { g, Tool } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { ShapeSelectionHandler } from './handlers/shape-selection-handler';
import { PolySelectionHandler } from './handlers/poly-selection-handler';
import { DrawingToolsHandler } from './handlers/drawing-tools-handler';
import { VectorToolsHandler } from './handlers/vector-tools-handler';
import { KeyboardHandler } from './handlers/keyboard-handler';
import { FinishHandler } from './handlers/finish-handler';
import { SecondaryToolsHandler } from './handlers/secondary-tools-handler';
import { getActiveLayer } from './layers';
import { zoomIn, zoomOut, applyZoom } from './zoom';

// Modular Refactored Imports
import { updateCanvasCursor } from './cursor-manager';
import { updateMouseCoordinates } from './coordinate-utils';
import { 
    selectTool, 
    undoAction, 
    redoAction, 
    clearSelectionContent 
} from './tool-manager';

export { 
    selectTool, 
    undoAction, 
    redoAction, 
    clearSelectionContent 
};

/**
 * @function onCanvasMouseDown
 * @description Master handler for canvas mousedown events.
 * @param {MouseEvent} event - The native mouse event.
 */
export function onCanvasMouseDown(event: MouseEvent): void {
    // [CRITICAL_LOGIC_START]
    g.startX = g.pX;
    g.startY = g.pY;
    (g as any).mouseDownX = g.pX;
    (g as any).mouseDownY = g.pY;
    
    // TOOL SPECIFIC: Eye Dropper Pick (Bug Fix)
    if (g.current_tool.id === Tool.Eye_Dropper.id) {
        if (typeof (window as any).handleEyeDropper === 'function') {
            (window as any).handleEyeDropper(event);
        }
        return;
    }

    // Bug 52: Pan Initiation (Middle Mouse OR Pan Tool selected)
    // Refinement #2074: On touch devices, we only pan with 2 fingers. 
    // Button 0 (left click/1-finger) now ONLY pans if it's NOT a touch event, OR if specifically desired.
    const isPanToolActive: boolean = g.current_tool.id === Tool.Pan.id;
    const isMouse: boolean = (event as any).pointerType === 'mouse' || (event instanceof MouseEvent && !(event as any).pointerType);
    
    if (event.button === 1 || (event.button === 0 && isPanToolActive && isMouse)) {
        const scrollArea = document.getElementById('canvasScrollArea');
        if (scrollArea) {
            (g as any).isPanning = true;
            (g as any).panStartX = event.clientX;
            (g as any).panStartY = event.clientY;
            (g as any).scrollStartX = scrollArea.scrollLeft;
            (g as any).scrollStartY = scrollArea.scrollTop;
            updateCanvasCursor(); // Sets to 'grabbing' or 'all-scroll'
            event.preventDefault();
            return;
        }
    }

    // Backup current raster state for undo before action
    const activeLayer = getActiveLayer();
    if (activeLayer && activeLayer.ctx && activeLayer.canvas) {
        const canW = activeLayer.canvas.width;
        const canH = activeLayer.canvas.height;
        g.previousLayerState = activeLayer.ctx.getImageData(0, 0, canW, canH);
    }

    // Quick Eyedropper Alt-Key Shortcut
    const isSpecialSelection: boolean = g.current_tool.id === Tool.Lasso.id || g.current_tool.id === Tool.PolySelect.id;
    if (event.altKey && !isSpecialSelection) {
        if (SecondaryToolsHandler.handleMouseDown(event)) return;
    }

    // Path Node Editor — intercept before other handlers
    if (g.current_tool.id === 'btn-path-edit') {
        const editor = (window as any).pathNodeEditor;
        if (editor) {
            const hit = editor.onMouseDown(g.pX, g.pY);
            if (hit) return;
        }
    }

    // Delegating to tool-specific handlers
    if (ShapeSelectionHandler.handleMouseDown(event)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', event);
        return;
    }
    if (PolySelectionHandler.handleMouseDown(event)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', event);
        return;
    }
    if (SecondaryToolsHandler.handleMouseDown(event)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', event);
        return;
    }
    if (DrawingToolsHandler.handleMouseDown(event)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', event);
        return;
    }
    if (VectorToolsHandler.handleMouseDown(event)) {
        EventBus.emit('CANVAS_MOUSE_DOWN', event);
        return;
    }

    EventBus.emit('CANVAS_MOUSE_DOWN', event);
    // [CRITICAL_LOGIC_END]
}

/**
 * @function onCanvasMouseMove
 * @description Master handler for canvas mousemove events.
 * @param {MouseEvent} event - The native mouse event.
 */
export function onCanvasMouseMove(event: MouseEvent): void {
    updateMouseCoordinates(event);
    
    // Bug 52: Middle Mouse Pan Progress
    if ((g as any).isPanning) {
        const scrollArea = document.getElementById('canvasScrollArea');
        if (scrollArea) {
            const dx = event.clientX - (g as any).panStartX;
            const dy = event.clientY - (g as any).panStartY;
            scrollArea.scrollLeft = (g as any).scrollStartX - dx;
            scrollArea.scrollTop = (g as any).scrollStartY - dy;
            updateCanvasCursor(); // Might set to 'grabbing'
            return;
        }
    }

    if (ShapeSelectionHandler.handleMouseMove(event)) {
        updateCanvasCursor();
        EventBus.emit('RENDER_LAYERS', undefined);
        EventBus.emit('CANVAS_MOUSE_MOVE', event);
        return;
    }
    if (PolySelectionHandler.handleMouseMove(event)) {
        updateCanvasCursor();
        EventBus.emit('RENDER_LAYERS', undefined);
        EventBus.emit('CANVAS_MOUSE_MOVE', event);
        return;
    }
    
    // TOOL SPECIFIC: Eye Dropper Hover/Pick (Bug Fix)
    if (g.current_tool.id === Tool.Eye_Dropper.id && event.buttons === 1) {
        if (typeof (window as any).handleEyeDropper === 'function') {
            (window as any).handleEyeDropper(event);
        }
        return;
    }

    // Path Node Editor mousemove
    if (g.current_tool.id === 'btn-path-edit') {
        const editor = (window as any).pathNodeEditor;
        if (editor) {
            editor.onMouseMove(g.pX, g.pY);
            (g as any).needsRender = true;
            return;
        }
    }

    if (DrawingToolsHandler.handleMouseMove(event)) {
        updateCanvasCursor();
        EventBus.emit('CANVAS_MOUSE_MOVE', event);
        return;
    }

    // Always trigger render for hover preview cursors if a drawing tool is active
    const drawingTools: readonly string[] = [Tool.Pen.id, Tool.Brush.id, Tool.Eraser.id, Tool.Spray.id, 'btn-red-eye'];
    if (drawingTools.includes(g.current_tool.id)) {
        (g as any).needsRender = true;
    }

    if (VectorToolsHandler.handleMouseMove(event)) {
        updateCanvasCursor();
        EventBus.emit('CANVAS_MOUSE_MOVE', event);
        return;
    }

    updateCanvasCursor();
    EventBus.emit('CANVAS_MOUSE_MOVE', event);
}

export function onCanvasMouseUp(event: MouseEvent): void {
    // Path Node Editor mouseup
    if (g.current_tool.id === 'btn-path-edit') {
        const editor = (window as any).pathNodeEditor;
        if (editor) {
            editor.onMouseUp();
            return;
        }
    }

    if (ShapeSelectionHandler.handleMouseUp(event)) {
        updateCanvasCursor();
        return;
    }
    if (PolySelectionHandler.handleMouseUp(event)) {
        updateCanvasCursor();
        return;
    }
    if (FinishHandler.finishDrawing(event)) {
        updateCanvasCursor();
        return;
    }
}

/**
 * @function onCanvasDoubleClick
 * @description Master handler for canvas double-click events.
 * @param {MouseEvent} event - The native mouse event.
 */
export function onCanvasDoubleClick(event: MouseEvent): void {
    // Activate path node editor when double-clicking a selected path shape
    if (g.current_tool.id === Tool.VectorSelect.id) {
        const vtm = (window as any).vectorToolManager;
        if (vtm?.editingTarget) {
            const { layerIndex, shapeIndex } = vtm.editingTarget;
            const layers = (window as any).layers;
            const layer  = layers?.[layerIndex];
            const shape  = layer?.shapes?.[shapeIndex];
            if (shape?.type === 'path' && shape.pathData) {
                EventBus.emit('ACTIVATE_PATH_EDIT', {
                    shapeId:    shape.id,
                    layerIndex,
                    pathData:   shape.pathData,
                });
                return;
            }
        }
    }

    if (PolySelectionHandler.handleDoubleClick(event)) {
        updateCanvasCursor();
        return;
    }
}

// Gestures State (#2074-Gestures)
const activePointers = new Map<number, PointerEvent>();
let initialGestureDist = 0;
let initialGestureAngle = 0;
let initialGestureCenter = { x: 0, y: 0 };
let initialZoom = 1;
let initialRotation = 0;
let isGestureActive = false;

/**
 * @function onCanvasPointerDown
 * @description Master handler for canvas pointerdown events.
 */
export function onCanvasPointerDown(event: PointerEvent): void {
    // Only use hardware pressure for actual pen/stylus; mouse always gives 0.5 which distorts size
    (window as any)._currentPressure = event.pointerType === 'pen' ? (event.pressure || 1.0) : 1.0;
    activePointers.set(event.pointerId, event);
    
    // [gesture-fix] Block browser native scrolling/shifting
    if (event.pointerType === 'touch') {
        // We don't preventDefault for all pointerdown to avoid breaking focus,
        // but for canvas it's essential.
        event.preventDefault();
    }

    // Switch to gesture mode if 2 pointers are active
    if (activePointers.size === 2) {
        startGesture(event);
        return;
    }
    
    // If we have more than 2, or already in gesture, ignore for drawing
    if (activePointers.size > 1 || isGestureActive) return;

    // Standard 1-finger / Mouse logic
    updateMouseCoordinates(event);
    onCanvasMouseDown(event as any);
}

function startGesture(event: PointerEvent): void {
    isGestureActive = true;
    const pointers = Array.from(activePointers.values());
    const p1 = pointers[0];
    const p2 = pointers[1];
    
    initialGestureDist = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
    initialGestureAngle = Math.atan2(p2.clientY - p1.clientY, p2.clientX - p1.clientX);
    initialGestureCenter = {
        x: (p1.clientX + p2.clientX) / 2,
        y: (p1.clientY + p2.clientY) / 2
    };
    initialZoom = g.zoom || 1;
    initialRotation = (g as any).canvasRotation || 0;
    
    // Cancel any active drawing
    (g as any).drawing = false;
    (g as any).isPanning = false;
}

/**
 * @function onCanvasPointerMove
 */
export function onCanvasPointerMove(event: PointerEvent): void {
    (window as any)._currentPressure = event.pointerType === 'pen' ? (event.pressure || 1.0) : 1.0;
    activePointers.set(event.pointerId, event);

    if (activePointers.size === 2 && isGestureActive) {
        handleGestureMove(event);
        event.preventDefault();
        return;
    }

    if (activePointers.size === 1 && !isGestureActive) {
        if (event.pointerType === 'touch') event.preventDefault();
        updateMouseCoordinates(event);
        onCanvasMouseMove(event as any);
    }
}

function handleGestureMove(event: PointerEvent): void {
    const pointers = Array.from(activePointers.values());
    const p1 = pointers[0];
    const p2 = pointers[1];

    if (!p1 || !p2) return;

    // 1. Zoom (Pinch)
    const currentDist = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
    const zoomFactor = currentDist / initialGestureDist;
    const newZoom = initialZoom * zoomFactor;
    g.zoom = Math.max(0.05, Math.min(newZoom, 32));

    // 2. Rotate (Twist)
    const currentAngle = Math.atan2(p2.clientY - p1.clientY, p2.clientX - p1.clientX);
    let angleDiff = (currentAngle - initialGestureAngle) * (180 / Math.PI);
    let newRotation = initialRotation + angleDiff;
    
    // Support Snap to 0 (+/- 5 degrees)
    if (Math.abs(newRotation % 360) < 5 || Math.abs(newRotation % 360) > 355) {
        newRotation = 0;
    }
    (g as any).canvasRotation = newRotation;

    // 3. Pan (Translation)
    const currentCenter = {
        x: (p1.clientX + p2.clientX) / 2,
        y: (p1.clientY + p2.clientY) / 2
    };
    
    const dx = currentCenter.x - initialGestureCenter.x;
    const dy = currentCenter.y - initialGestureCenter.y;
    
    const scrollArea = document.getElementById('canvasScrollArea');
    if (scrollArea) {
        // We apply a relative shift to the scroll based on center movement
        // Refinement: Increased sensitivity for smoother experience
        const scrollSensitivity = 1.0;
        scrollArea.scrollLeft -= dx * scrollSensitivity;
        scrollArea.scrollTop -= dy * scrollSensitivity;
        
        // Update initial center for next delta to avoid exponential speed
        initialGestureCenter = currentCenter;
    }

    applyZoom(g.zoom, null); // Refreshes CSS transforms
    event.preventDefault(); // CRITICAL: Stop browser from scrolling during gesture
}

/**
 * @function onCanvasPointerUp
 */
export function onCanvasPointerUp(event: PointerEvent): void {
    activePointers.delete(event.pointerId);
    
    if (activePointers.size < 2) {
        isGestureActive = false;
    }

    if (activePointers.size === 0) {
        onCanvasMouseUp(event as any);
    }
}

/**
 * @function attachCanvasEvents
 * @description Binds native DOM event listeners to the canvas element.
 * @param {HTMLCanvasElement} [canvasElement] - The canvas to bind events to.
 */
export function attachCanvasEvents(canvasElement?: HTMLCanvasElement): void {
    const target = canvasElement || (g as any).canvas;
    if (!target) return;

    // Switch to unified PointerEvents for multi-touch support
    target.addEventListener('pointerdown', onCanvasPointerDown);
    target.addEventListener('pointermove', onCanvasPointerMove);
    target.addEventListener('pointerup', onCanvasPointerUp);
    target.addEventListener('pointercancel', onCanvasPointerUp);
    target.addEventListener('pointerleave', onCanvasPointerUp);

    target.ondblclick = onCanvasDoubleClick;
    initWheelZoomListener(target);
}

/**
 * @function initWheelZoomListener
 * @description Sets up a non-passive wheel event listener for internal zooming.
 * @private
 */
function initWheelZoomListener(canvasElement: HTMLCanvasElement): void {
    const scrollArea = document.getElementById('canvasScrollArea') as HTMLElement | null;
    const target = scrollArea || canvasElement;
    const key = '__hcie_canvas_wheel_zoom_attached__';
    
    if (!(window as any)[key]) {
        (window as any)[key] = true;
        target.addEventListener('wheel', (event: WheelEvent) => {
            const currentTarget = event.target as HTMLElement | null;
            if (!currentTarget) return;

            const targetTagName = currentTarget.tagName?.toLowerCase();
            if (['input', 'textarea', 'select'].includes(targetTagName)) return;

            event.preventDefault();
            if (event.deltaY < 0) zoomIn(event);
            else if (event.deltaY > 0) zoomOut(event);
        }, { passive: false });
    }
}

// Global window event listeners for input stability
if (typeof window !== 'undefined') {
    window.addEventListener('keydown', (event: KeyboardEvent) => KeyboardHandler.handleKeyDown(event));
    window.addEventListener('mouseup', (event: MouseEvent) => {
        if (g.drawing || g.movingSelection) onCanvasMouseUp(event);
        (g as any).isPanning = false;
        updateCanvasCursor();
    });
}

/**
 * @function undoImage
 */
export function undoImage(): void {
    undoAction();
}

/**
 * @function redoImage
 */
export function redoImage(): void {
    redoAction();
}

// Legacy Exposure
(window as any).finishDrawing = (event: MouseEvent | null) => FinishHandler.finishDrawing(event);
(window as any).attachCanvasEvents = attachCanvasEvents;
(window as any).selectTool = selectTool;
(window as any).undoImage = undoImage;
(window as any).redoImage = redoImage;
(window as any).zoomIn = (event: MouseEvent | null) => zoomIn(event);
(window as any).zoomOut = (event: MouseEvent | null) => zoomOut(event);
(window as any).applyZoom = (oldZoom: number, event: MouseEvent | null) => applyZoom(oldZoom, event);
